import React, { useState, useEffect } from "react";
import { PreviewModal } from "../UI/PreviewModal";

interface Template {
  id: string;
  exhibitId: string;
  templateId: string;
  name: string;
  description: string;
  image: string;
  features: string[];
  areaId?: string;
  orgId?: string;
  previewId?: string;
  profileType?: string;
  templatePath?: string;
}

interface TemplateSelectionProps {
  formData: {
    selectedTemplate?: string;
    templateExhibitId?: string;
    template?: string;
    templatePath?: string;
  };
  onUpdate: (field: string, value: any) => void;
  errors: { [key: string]: string };
  clientType: string;
}

  const templateId = String(process.env.REACT_APP_TEMPLATE_ID_LOCATION);
  const johnExhibitId = String(process.env.REACT_APP_JOHN_EXHIBIT_ID);
  const juliaExhibitId = String(process.env.REACT_APP_JULIA_EXHIBIT_ID);
  const charleyExhibitId = String(process.env.REACT_APP_CHARLEY_EXHIBIT_ID);
export const AVAILABLE_TEMPLATES = [
  {
    id: "JuliaModernProfessional",
    exhibitId: juliaExhibitId,
    templateId: templateId,
    name: "Modern Professional",
    description:
      "A delicate design and stylish tones make a lasting impression with charm and grace.",
    image: "JuliaTemplate.png",
    previewId: "template/JuliaPreview.html",
    features: [
      "✓ Networkers",
      "✓ Marketers",
      "✓ Personal Brand",
      "✓ Influencers",
      "✓ Advisors",
      "✓ Consultants",
    ],
    templatePath: "JuliaTemplate_Scoped.html",
  },
  {
    id: "JohnConfidentAndBold",
    exhibitId: johnExhibitId,
    templateId: templateId,
    name: "Confident and Bold",
    description:
      "A bold, modern design that values clarity, authority, and refined presentation",
    image: "JohnTemplate.png",
    previewId: "template/JohnPreview.html",
    features: [
      "✓ Speakers",
      "✓ Influencers",
      "✓ Creatives",
      "✓ Specialists",
      "✓ Tech Pros",
      "✓ Teams",
    ],
    templatePath: "JohnTemplate_Scoped.html",
  },
  {
    id: "CharleyCircleProfessional",
    exhibitId: charleyExhibitId,
    templateId: templateId,
    name: "Circle Pro",
    description:
      "A warm and inviting design that brings warmth and trust to your professional profile",
    image: "CharleyTemplate.png",
    previewId: "template/CharleyPreview.html",
    features: [
      "✓ Collaborative Professionals",
      "✓ Recruiters",
      "✓ Entrepreneurs",
      "✓ Creative Consultants",
      "✓ Designers",
      "✓ Real Estate",
    ],
    templatePath: "CharleyTemplate_Scoped.html",
  },
];
export const TemplateSelection: React.FC<TemplateSelectionProps> = ({
  formData,
  onUpdate,
  errors,
  clientType,
}) => {
  const [previewModal, setPreviewModal] = useState<{
    isOpen: boolean;
    exhibitId: string;
    templateName: string;
  }>({
    isOpen: false,
    exhibitId: "",
    templateName: "",
  });

  // console.log("templateId", templateId);
  const [templates, setTemplates] = useState<Template[]>([]);

  // Custom loading of templates based on client type
  useEffect(() => {
    const loadTemplates = () => {
      if (
        clientType &&
        (clientType === "KlinTeam" || clientType === "Onboarding")
      ) {
        setTemplates([
          {
            id: "68b9c127aac99217e80f074d", // location ID
            exhibitId: "68b9c9bd0d1ad6ed9a2f9abf",
            templateId: "68b9c127aac99217e80f074d",
            name: "Profile Minified",
            description: "Clean, minimalist profile with focus on content",
            image: "simple_profile.png",
            features: ["✓ Profile", "✓ Contact Details", "✓ Social Media"],
            areaId: "68b9c139aac99217e80f0750",
            orgId: "68b9c127aac99217e80f074d",
            previewId: "68b9f5c93213d4cdf706f6c4", // Well looking completed profile
            profileType: "singlePage",
          },
        ]);
      } else {
        setTemplates(AVAILABLE_TEMPLATES);
      }
    };

    loadTemplates();
  }, [clientType]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTemplateSelect = (template: Template) => {
    onUpdate("selectedTemplate", template.name);
    onUpdate("templateExhibitId", template.exhibitId);
    onUpdate("template", template.templateId);
    if (template.templatePath) {
      onUpdate("templatePath", template.templatePath);
    }
    if (template.areaId) {
      onUpdate("areaId", template.areaId);
    }
    if (template.orgId) {
      onUpdate("orgId", template.orgId);
    }
  };

  const handlePreview = (template: Template) => {
    setPreviewModal({
      isOpen: true,
      exhibitId: template.previewId || "",
      templateName: template.name,
    });
  };

  const closePreview = () => {
    setPreviewModal({
      isOpen: false,
      exhibitId: "",
      templateName: "",
    });
  };

  const isSelected = (template: Template) => {
    return formData.selectedTemplate === template.name;
  };

  return templates.length > 0 ? (
    <div className="form-step active">
      <p style={{ textAlign: "center", color: "#666", marginBottom: "2rem" }}>
        Select a template that best represents your professional style
      </p>

      <div className="template-grid">
        {templates.map((template) => (
          <div
            key={template.id}
            className={`template-option ${isSelected(template) ? "selected" : ""}`}
            data-template={template.templateId}
            data-template-exhibit-id={template.exhibitId}
            data-template-area-id={template?.areaId || ""}
            onClick={() => handleTemplateSelect(template)}
            style={{ cursor: "pointer" }}
          >
            <div className="template-preview">
              <img
                src={`images/${template.image}`}
                alt={`${template.name} Template`}
                className="template-image"
              />
              {/* Preview Button Overlay */}
              <div className="template-preview-overlay">
                <button
                  className="preview-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handlePreview(template);
                  }}
                  aria-label={`Preview ${template.name} template`}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 12S5 4 12 4S23 12 23 12S19 20 12 20S1 12 1 12Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle
                      cx="12"
                      cy="12"
                      r="3"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Preview
                </button>
              </div>
            </div>
            <div className="template-details">
              <h2>{template.name}</h2>
              <p>{template.description}</p>
              <div className="template-features">
                <hr className="mt-1 mb-1" />
                <h4>Great for:</h4>
                <ul>
                  {template.features.map((feature, index) => (
                    <li key={index}>{feature}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Preview Modal */}
      <PreviewModal
        isOpen={previewModal.isOpen}
        onClose={closePreview}
        exhibitId={previewModal.exhibitId}
        templateName={previewModal.templateName}
      />
    </div>
  ) : (
    <div className="form-step active">
      <p style={{ textAlign: "center", color: "#666", marginBottom: "2rem" }}>
        No templates found for this client type
      </p>
    </div>
  );
};
